import type { Board, Pos, MatchPair } from "./types";
import { cloneBoard, boardDims } from "./board";
import { findMatchInBoard } from "./matching";

// 从 (x,y) 沿 (dx,dy) 方向收集一段连续的非空方块。
// 这一整组会作为一个整体被玩家推动（贪吃蛇式的「一推一串」）。
export function getGroup(
  x: number,
  y: number,
  dx: number,
  dy: number,
  board: Board,
): Pos[] {
  const { w, h } = boardDims(board);
  const res: Pos[] = [];
  let cx = x;
  let cy = y;
  while (cx >= 0 && cx < w && cy >= 0 && cy < h && board[cy][cx] !== null) {
    res.push({ x: cx, y: cy });
    cx += dx;
    cy += dy;
  }
  return res;
}

// 该组沿 (dx,dy) 最多能滑动几格（被边界或其它方块挡住为止）。
export function getMaxSteps(
  group: Pos[],
  dx: number,
  dy: number,
  board: Board,
): number {
  if (!group.length) return 0;
  const { w, h } = boardDims(board);
  const last = group[group.length - 1];
  let cx = last.x + dx;
  let cy = last.y + dy;
  let steps = 0;
  while (cx >= 0 && cx < w && cy >= 0 && cy < h && board[cy][cx] === null) {
    steps++;
    cx += dx;
    cy += dy;
  }
  return steps;
}

// 把一组方块沿 (dx,dy) 平移，返回新棋盘（不修改入参）。
export function applySlideMove(
  board: Board,
  group: Pos[],
  dx: number,
  dy: number,
): Board {
  const { w, h } = boardDims(board);
  const next = cloneBoard(board);
  const values = group.map((p) => ({ ...p, val: board[p.y][p.x] }));
  for (const p of group) next[p.y][p.x] = null;
  for (const item of values) {
    const nx = item.x + dx;
    const ny = item.y + dy;
    if (nx >= 0 && nx < w && ny >= 0 && ny < h) next[ny][nx] = item.val;
  }
  return next;
}

// 设计规则：消除必须发生在玩家「抓住」的那个方块上。
// 检查 (gx,gy) 处的方块滑到位后能否与某个同类直线配对。
export function findGrabbedMatch(
  board: Board,
  gx: number,
  gy: number,
): MatchPair | null {
  const { w, h } = boardDims(board);
  if (gx < 0 || gx >= w || gy < 0 || gy >= h) return null;
  const val = board[gy][gx];
  if (val === null) return null;
  const mate = findMatchInBoard(board, gx, gy, val);
  return mate ? { a: { x: gx, y: gy }, b: mate } : null;
}
